import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { TOOL_DEFINITIONS, type ToolDefinition } from '../config/toolAccess';
import { buildToolShareAbsoluteUrl } from '../utils/toolShareUrl';
import { trackAnalyticsEvent } from '../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../services/eventTaxonomy';
import type { ToolDirectoryCategory, ToolId } from '../services/eventTypes';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../utils/iconFallbacks';
const TOOL_ID_TO_ANALYTICS_ID: Record<string, ToolId> = {
  fleetOptimisation: 'fleet_optimisation',
  postcodeStopAssignment: 'postcode_stop_assignment',
};

function toAnalyticsToolId(toolId: string): ToolId | null {
  return TOOL_ID_TO_ANALYTICS_ID[toolId] ?? null;
}

const ALLOWED_DIRECTORY_CATEGORIES: ReadonlySet<ToolDirectoryCategory> = new Set<ToolDirectoryCategory>([
  'all',
  'financial',
  'environment',
  'routing',
  'geocoding',
]);

function toDirectoryCategory(value: string): ToolDirectoryCategory | null {
  return ALLOWED_DIRECTORY_CATEGORIES.has(value as ToolDirectoryCategory)
    ? (value as ToolDirectoryCategory)
    : null;
}

const categoryIconSx = { width: 28, height: 28 };

const ToolsPage: React.FC = () => {
  const { t } = useTranslation('tools');
  const navigate = useNavigate();
  const visibleTools = TOOL_DEFINITIONS;
  const [categoryFilter, setCategoryFilter] = React.useState<string>('all');
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [shareTool, setShareTool] = React.useState<ToolDefinition | null>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = React.useState(false);
  const [copyError, setCopyError] = React.useState<string | null>(null);
  const linkInputRef = React.useRef<HTMLInputElement>(null);

  const categoryIconMap: Record<string, React.ReactElement> = {
    financial: <Icon size={20} d={ICON_FALLBACKS.AttachMoney} style={categoryIconSx} aria-label={t('categoryTitle.financial')} />,
    environment: <Icon size={20} d={ICON_FALLBACKS.EnergySavingsLeaf} style={categoryIconSx} aria-label={t('categoryTitle.environment')} />,
    routing: <Icon size={20} name="route" style={categoryIconSx} aria-label={t('categoryTitle.routing')} />,
    geocoding: <Icon size={20} d={ICON_FALLBACKS.Language} style={categoryIconSx} aria-label={t('categoryTitle.geocoding')} />,
  };

  const getCategoryIcon = (category: string): React.ReactElement => {
    return categoryIconMap[category.toLowerCase()] ?? (
      <Icon size={20} d={ICON_FALLBACKS.BuildCircle} style={categoryIconSx} aria-label={t('categoryTitle.tool')} />
    );
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const categories = Array.from(new Set(visibleTools.map((tool) => tool.category))).sort();

  const filteredTools = visibleTools.filter((tool) => {
    const matchesCategory = categoryFilter === 'all' || tool.category === categoryFilter;
    if (!matchesCategory) {
      return false;
    }

    if (!normalizedSearchTerm) {
      return true;
    }

    const searchableText = [
      tool.title,
      tool.description,
      tool.category,
      tool.createdBy,
      tool.isPublic ? 'public' : 'private',
    ]
      .join(' ')
      .toLowerCase();

    return searchableText.includes(normalizedSearchTerm);
  });

  const previousFilterRef = React.useRef<string>('all');
  const lastSearchTrackedRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!normalizedSearchTerm) {
      lastSearchTrackedRef.current = '';
      return;
    }
    const handle = window.setTimeout(() => {
      if (lastSearchTrackedRef.current === normalizedSearchTerm) {
        return;
      }
      lastSearchTrackedRef.current = normalizedSearchTerm;
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.tool_directory_searched, {
        query_length: normalizedSearchTerm.length,
        result_count: filteredTools.length,
        timestamp: new Date().toISOString(),
      });
    }, 500);
    return () => window.clearTimeout(handle);
  }, [normalizedSearchTerm, filteredTools.length]);

  React.useEffect(() => {
    if (categoryFilter === previousFilterRef.current) {
      return;
    }
    previousFilterRef.current = categoryFilter;
    const category = toDirectoryCategory(categoryFilter);
    if (!category) {
      return;
    }
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.tool_directory_filtered, {
      category,
      result_count: filteredTools.length,
      timestamp: new Date().toISOString(),
    });
  }, [categoryFilter, filteredTools.length]);

  const shareUrl = shareTool ? buildToolShareAbsoluteUrl(shareTool.path) : '';

  const closeShareDialog = () => {
    setShareTool(null);
    setCopyError(null);
  };

  const trackShareCopy = (fallbackUsed: boolean, succeeded: boolean): void => {
    const analyticsToolId = shareTool ? toAnalyticsToolId(shareTool.id) : null;
    if (!analyticsToolId) {
      return;
    }
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.tool_share_link_copied, {
      tool_id: analyticsToolId,
      fallback_used: fallbackUsed,
      succeeded,
      timestamp: new Date().toISOString(),
    });
  };

  const copyShareLink = async () => {
    setCopyError(null);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopySnackbarOpen(true);
        trackShareCopy(false, true);
        return;
      }
    } catch {
      // fall through to legacy copy
    }

    const input = linkInputRef.current;
    if (input) {
      input.focus();
      input.select();
      input.setSelectionRange(0, shareUrl.length);
      try {
        const ok = document.execCommand('copy');
        if (ok) {
          setCopySnackbarOpen(true);
          trackShareCopy(true, true);
          return;
        }
      } catch {
        // ignore
      }
    }
    setCopyError(t('share.copyManualFallback'));
    trackShareCopy(true, false);
  };

  const openShareInNewTab = () => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
    const analyticsToolId = shareTool ? toAnalyticsToolId(shareTool.id) : null;
    if (analyticsToolId) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.tool_share_opened_in_new_tab, {
        tool_id: analyticsToolId,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const openShareDialog = (tool: ToolDefinition) => {
    setShareTool(tool);
    const analyticsToolId = toAnalyticsToolId(tool.id);
    if (analyticsToolId) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.tool_share_opened, {
        tool_id: analyticsToolId,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const launchTool = (tool: ToolDefinition) => {
    const analyticsToolId = toAnalyticsToolId(tool.id);
    if (analyticsToolId) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.tool_launched, {
        tool_id: analyticsToolId,
        source: 'directory',
        timestamp: new Date().toISOString(),
      });
    }
    navigate(tool.path);
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ py: 4 }}
      data-testid="tools.page.root"
      data-loading="false"
      data-error={copyError ? 'true' : 'false'}
      data-empty={filteredTools.length === 0 ? 'true' : 'false'}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        {t('page.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('page.subtitle')}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label={t('filters.searchLabel')}
          placeholder={t('filters.searchPlaceholder')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          inputProps={{ 'data-testid': 'tools.search.input' }}
        />
        <FormControl sx={{ minWidth: { xs: '100%', md: 240 } }}>
          <InputLabel id="tool-category-filter-label">{t('filters.categoryLabel')}</InputLabel>
          <Select
            labelId="tool-category-filter-label"
            id="tool-category-filter"
            value={categoryFilter}
            label={t('filters.categoryLabel')}
            onChange={(event) => setCategoryFilter(event.target.value)}
            inputProps={{ 'data-testid': 'tools.filter.category' }}
          >
            <MenuItem value="all">{t('filters.allCategories')}</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Grid container spacing={2}>
        {filteredTools.map((tool) => {
          return (
            <Grid size={{ xs: 12, md: 6 }} key={tool.id}>
              <Card elevation={1}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        icon={<Icon size={20} name="user" />}
                        label={t('card.createdBy', { name: tool.createdBy })}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        icon={tool.isPublic ? <Icon size={20} d={ICON_FALLBACKS.Public} /> : undefined}
                        label={tool.isPublic ? t('card.public') : t('card.private')}
                        color={tool.isPublic ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                    <Tooltip title={t('card.categoryIconTooltip', { category: tool.category })}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{getCategoryIcon(tool.category)}</Box>
                    </Tooltip>
                  </Box>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {tool.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('card.categoryLabel', { category: tool.category })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {tool.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Icon size={20} d={ICON_FALLBACKS.Share} />}
                  onClick={() => openShareDialog(tool)}
                  aria-label={t('card.shareAriaLabel', { title: tool.title })}
                  data-testid={`tools.card.${tool.id}.share`}
                >
                    {t('card.shareButton')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => launchTool(tool)}
                    aria-label={t('card.ctaAriaLabel', { cta: tool.ctaLabel, title: tool.title })}
                  >
                    {tool.ctaLabel}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {filteredTools.length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          {t('alerts.noResults')}
        </Alert>
      )}

      <Dialog
        open={Boolean(shareTool)}
        onClose={closeShareDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="tool-share-dialog-title"
        data-testid="tools.share.dialog"
        aria-hidden={shareTool ? 'false' : 'true'}
      >
        <DialogTitle id="tool-share-dialog-title" sx={{ pr: 6 }}>
          {t('share.dialogTitle')}
          <IconButton
            aria-label={t('share.closeDialog')}
            onClick={closeShareDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Icon size={20} name="xmark" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {shareTool && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Trans
                  i18nKey="share.description"
                  t={t}
                  values={{ title: shareTool.title }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
              <TextField
                inputRef={linkInputRef}
                label={t('share.linkLabel')}
                value={shareUrl}
                fullWidth
                multiline
                minRows={2}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              {copyError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {copyError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button variant="contained" startIcon={<Icon size={20} name="clipboard" />} onClick={() => void copyShareLink()} data-testid="tools.share.copy-action">
              {t('share.copyButton')}
            </Button>
            <Button variant="outlined" startIcon={<Icon size={20} name="arrow-up-right-from-square" />} onClick={openShareInNewTab} data-testid="tools.share.open-action">
              {t('share.openInNewTab')}
            </Button>
          <Button onClick={closeShareDialog}>{t('share.closeButton')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={4000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setCopySnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }} data-testid="tools.toast.copy-success">
          {t('share.copySuccess')}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ToolsPage;
